import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { MessageBody } from "@/pages/message-body-render";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { MessageAttachments } from "@/components/message-attachments";
import { PadsplitSendErrorDialog, isPadsplitSendError } from "@/components/padsplit-send-error-dialog";
import { AlertCircle, AtSign, Battery, Calendar, Check, CheckCircle, Circle, ChevronDown, ChevronUp, Clock, Edit, ExternalLink, HandCoins, Loader2, Megaphone, MessageCircle, MoreHorizontal, PhoneIncoming, PhoneMissed, PhoneOutgoing, Plus, RefreshCw, Send, Sparkles, Trash2, User, UserCheck, Voicemail, Wrench, X, Zap } from "lucide-react";
import { parseQuoCallMeta, describeQuoCallStatus, isMissedQuoCall, formatQuoCallDuration, type QuoCallView } from "./quo-call";
import type { Property, TeamMember } from "@shared/schema";
import type { MemberChatThread } from "@shared/schema";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { TimelineChat, WorkflowFields, attachmentUrlsFor, avatarColor, formatTime, initials } from "./_shared";
import { AutoDraftBubble } from "./AutoDraftBubble";
import { searchIncludes } from "@/lib/search-normalize";
import { extractMediaUrlsFromText } from "@/lib/inbox-media";
import {
  useResolveInvalidations,
  applyOptimisticInboxResolve,
  rollbackOptimisticInboxResolve,
  decrementInboxRowUpdate,
  incrementInboxRowUpdate,
} from "./_resolve-invalidations";
import { invalidateWaitingReplyQueries } from "./_waiting-reply-invalidations";



// Escape regex metacharacters in a user-supplied mention token (e.g. a teammate
// first name containing "(") so building the mention RegExp can't throw.
const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Remove raw media URLs from message text when the same URLs are already
// rendered as thumbnails by MessageAttachments, so the chat bubble doesn't
// redundantly show both the URL string and the image preview.
function stripMediaUrls(text: string): string {
  const urls = extractMediaUrlsFromText(text);
  if (urls.length === 0) return text;
  let cleaned = text;
  for (const url of urls) {
    cleaned = cleaned.replace(url, "");
  }
  // Collapse multiple consecutive whitespace / newlines left by removal.
  return cleaned.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
}

// ─── AttributionChip — "Sent by …" automation attribution ────────────────────
// Task #4266. Appears only on admin bubbles produced by an automated process
// (messaging rule or low-battery cron). Clickable: low_battery → deep-links to
// the Low Battery Auto-Notification settings block; rule → rule editor page.
function AttributionChip({
  attribution,
  chatId,
}: {
  attribution: NonNullable<TimelineChat["automationAttribution"]>;
  chatId: number;
}) {
  const [, navigate] = useLocation();
  const isLowBattery = attribution.source === "low_battery";
  // Task #4712 — after-hours business-hours auto-reply: a distinct subtle
  // note ("Auto-reply (business hours)") instead of the generic rule chip.
  const isBusinessHours = attribution.source === "business_hours";
  // Task #4830 — automated collection notice. These go to members about money
  // they owe, so it matters that the thread reads unambiguously as "the system
  // sent this, from a collections rule" rather than as a message a person
  // typed. Deep-links to the property's collections settings.
  const isCollections = attribution.source === "collections";
  // A reply the AI wrote and sent on its own (Messages Auto Draft). Not
  // clickable: there is no per-message rule to open, and drafts the host
  // edited were re-sent manually so they never reach this branch.
  const isAutoDraft = attribution.source === "auto_draft";
  const label = isLowBattery
    ? "Low Battery Auto-Notification"
    : isBusinessHours
      ? "Auto-reply (business hours)"
      : isCollections
        ? "Collections Rule"
        : isAutoDraft
          ? "Auto-draft · AI"
          : attribution.ruleName
            ? `Rule: ${attribution.ruleName}`
            : "Automation Rule";
  const href = isLowBattery
    ? "/app/devices/smart-locks?tab=settings&highlight=low_battery"
    : isCollections
      ? `/app/financials/collections?tab=properties${attribution.propertyId != null ? `&property=${attribution.propertyId}` : ""}`
      : isAutoDraft
        ? null
        : attribution.ruleId != null
          ? `/app/messages/rules/${attribution.ruleId}`
          : null;
  const text = isBusinessHours || isAutoDraft ? label : `Sent by ${label}`;
  return (
    <button
      type="button"
      onClick={href ? () => navigate(href) : undefined}
      className={`inline-flex items-center gap-0.5 rounded bg-white/20 px-1.5 py-px text-[9px] font-semibold uppercase leading-none tracking-wide text-white/90 hover:text-white transition-opacity ${href ? "cursor-pointer hover:bg-white/30" : "cursor-default"}`}
      title={
        isBusinessHours
          ? "This message was sent automatically because the member messaged outside your business hours."
          : isCollections
            ? "This payment-arrangement message was sent automatically by your Collections settings."
            : isAutoDraft
              ? "Written by AI and sent automatically by Auto Draft — no one edited it before it went out."
              : `Sent by ${label}`
      }
      data-testid={`chip-attribution-${chatId}`}
    >
      {isLowBattery ? (
        <Battery className="h-2.5 w-2.5" />
      ) : isBusinessHours ? (
        <Clock className="h-2.5 w-2.5" />
      ) : isCollections ? (
        <HandCoins className="h-2.5 w-2.5" />
      ) : isAutoDraft ? (
        <Sparkles className="h-2.5 w-2.5" />
      ) : (
        <Zap className="h-2.5 w-2.5" />
      )}
      {text}
    </button>
  );
}


// ─── ThreadNote — renders a single internal note with @mention highlighting ─
export function ThreadNote({
  note,
  isFocused,
  teamMembers = [],
  currentUserId,
  chatId,
  memberId,
}: {
  note: MemberChatThread;
  isFocused: boolean;
  /** Used to resolve mention IDs in the note to real @tokens for accurate highlighting. */
  teamMembers?: TeamMember[];
  /** Logged-in user's app-user id — used to show the author-only three-dot menu. */
  currentUserId?: number | null;
  /** The parent member_chat id — needed to invalidate the threads + timeline queries. */
  chatId: number;
  memberId: number;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const noteRef = useRef<HTMLDivElement | null>(null);
  const [highlight, setHighlight] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(note.body);
  const [editMentionChips, setEditMentionChips] = useState<
    Array<{ token: string; userId: number; displayName: string }>
  >([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isAuthor = currentUserId != null && note.authorId === currentUserId;

  // Scroll a deep-linked note (?thread=N) into view and flash the highlight.
  //
  // This used to be a fixed 80ms defer + behavior:"smooth", which is the exact
  // pattern Task #2338 already replaced for chat bubbles: the thread panel's
  // notes render after their fetch resolves and the timeline prepends older
  // pages underneath, so the note's offsetTop moved after the scroll target
  // was computed — and a mid-flight smooth animation gets cancelled outright
  // by the timeline's scrollTop restore. The result was a scroll that stopped
  // short of the note and a highlight that expired before it was ever visible.
  //
  // Same treatment as the bubble scroller: wait for offsetTop to hold steady
  // for two consecutive frames (3s ceiling), scroll instantly so nothing can
  // interrupt an animation, and only start the highlight timer once the
  // scroll has actually fired.
  useEffect(() => {
    if (!isFocused) return;
    const node = noteRef.current;
    if (!node) return;

    let cancelled = false;
    let rafId: number | null = null;
    let highlightTimer: ReturnType<typeof setTimeout> | null = null;

    const STABLE_FRAMES_NEEDED = 2;
    const MAX_WAIT_MS = 3000;
    const startedAt = Date.now();
    let lastOffsetTop = -Infinity;
    let stableFrames = 0;

    function doScroll() {
      if (cancelled) return;
      node!.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "center" });
      setHighlight(true);
      highlightTimer = setTimeout(() => {
        if (!cancelled) setHighlight(false);
      }, 2400);
    }

    function checkStability() {
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        doScroll();
        return;
      }
      const currentOffsetTop = node!.offsetTop;
      if (Math.abs(currentOffsetTop - lastOffsetTop) < 1) stableFrames++;
      else stableFrames = 0;
      lastOffsetTop = currentOffsetTop;
      if (stableFrames >= STABLE_FRAMES_NEEDED) doScroll();
      else rafId = requestAnimationFrame(checkStability);
    }

    const initialTimer = setTimeout(() => {
      if (!cancelled) rafId = requestAnimationFrame(checkStability);
    }, 50);

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      if (highlightTimer) clearTimeout(highlightTimer);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [isFocused, note.id]);

  function startEdit() {
    setEditBody(note.body);
    // Pre-populate mention chips from the stored mention IDs so that existing
    // @mentions in the body are preserved (and visible) when the editor opens.
    const preChips: Array<{ token: string; userId: number; displayName: string }> = [];
    if (Array.isArray(note.mentions) && note.mentions.length > 0) {
      for (const uid of note.mentions) {
        const tm = teamMembers.find(
          (m) => m.inviteeUserId === uid || (m.id === 0 && m.userId === uid),
        );
        if (tm) {
          const fullName = tm.name ?? String(tm.email ?? "").split("@")[0];
          const firstName = fullName.split(/\s+/)[0];
          preChips.push({ token: firstName, userId: uid, displayName: fullName });
        }
      }
    }
    setEditMentionChips(preChips);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setEditBody(note.body);
    setEditMentionChips([]);
  }

  // Close editor on Escape
  useEffect(() => {
    if (!editing) return;
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") cancelEdit(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [editing]);

  const editMutation = useMutation({
    mutationFn: async () => {
      const presentChips = editMentionChips.filter((c) =>
        new RegExp(`@${escapeRegExp(c.token)}(?:[\\s,.]|$)`).test(editBody),
      );
      const mentions = presentChips.map((c) => c.userId);
      const res = await apiRequest(
        "PATCH",
        `/api/app/member-chats/${chatId}/threads/${note.id}`,
        { body: editBody.trim(), ...(mentions.length > 0 ? { mentions } : {}) },
      );
      return res.json();
    },
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ["/api/app/member-chats", chatId, "threads"] });
    },
    onError: (err) => {
      toast({ title: "Could not save edit", description: parseErrorMessage(err, "Couldn't save edit. Please try again."), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/app/member-chats/${chatId}/threads/${note.id}`);
    },
    onSuccess: () => {
      setDeleteOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/app/member-chats", chatId, "threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
    },
    onError: (err) => {
      toast({ title: "Could not delete reply", description: parseErrorMessage(err, "Couldn't delete reply. Please try again."), variant: "destructive" });
    },
  });

  return (
    <>
      <div
        ref={noteRef}
        className={`group rounded border px-2 py-1.5 text-xs transition-all duration-500 ${
          highlight
            ? "border-purple-300 bg-purple-50 ring-1 ring-purple-400"
            : "border-gray-100 bg-white"
        }`}
        data-testid={`thread-note-${note.id}`}
      >
        <div className="flex items-start justify-between gap-1">
          <div className="font-medium text-gray-700">
            {note.authorName}{" "}
            <span className="font-normal text-gray-400">· {formatTime(note.createdAt)}</span>
            {note.editedAt && (
              <span className="ml-1 font-normal text-gray-400 italic" data-testid={`thread-edited-${note.id}`}>
                · edited
              </span>
            )}
          </div>
          {isAuthor && !editing && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 rounded p-0.5 text-gray-400 hover:text-gray-600 transition-opacity"
                  aria-label="Reply options"
                  data-testid={`thread-note-menu-${note.id}`}
                >
                  <MoreHorizontal className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="text-xs">
                <DropdownMenuItem
                  onClick={startEdit}
                  data-testid={`thread-note-edit-${note.id}`}
                >
                  <Edit className="mr-1.5 h-3 w-3" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDeleteOpen(true)}
                  className="text-red-600 focus:text-red-600"
                  data-testid={`thread-note-delete-${note.id}`}
                >
                  <Trash2 className="mr-1.5 h-3 w-3" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {editing ? (
          <div className="mt-1 space-y-1">
            <MentionNoteInput
              value={editBody}
              onChange={setEditBody}
              onMention={(token, userId, displayName) =>
                setEditMentionChips((prev) =>
                  prev.some((c) => c.userId === userId) ? prev : [...prev, { token, userId, displayName }],
                )
              }
              teamMembers={teamMembers}
              chatId={chatId}
              onSubmit={() => {
                if (editBody.trim() && !editMutation.isPending) editMutation.mutate();
              }}
            />
            {editMentionChips.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {editMentionChips.map((chip) => (
                  <span
                    key={chip.userId}
                    className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700"
                  >
                    @{chip.displayName}
                    <button
                      type="button"
                      onClick={() =>
                        setEditMentionChips((prev) => prev.filter((c) => c.userId !== chip.userId))
                      }
                      className="ml-0.5 text-purple-400 hover:text-purple-700"
                      aria-label={`Remove mention of ${chip.displayName}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-1 justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={cancelEdit}
                disabled={editMutation.isPending}
                data-testid={`thread-note-cancel-edit-${note.id}`}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-6 px-2 text-xs"
                disabled={!editBody.trim() || editMutation.isPending}
                onClick={() => editMutation.mutate()}
                data-testid={`thread-note-save-edit-${note.id}`}
              >
                {editMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-0.5 whitespace-pre-wrap text-gray-800">
            {renderNoteBody(note.body, note.mentions, teamMembers)}
          </div>
        )}
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete reply?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">This will permanently remove your reply. This action cannot be undone.</p>
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteMutation.isPending}
              data-testid={`thread-note-cancel-delete-${note.id}`}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              data-testid={`thread-note-confirm-delete-${note.id}`}
            >
              {deleteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ─── MentionNoteInput — textarea with @-trigger team member picker ─────────
export function MentionNoteInput({
  value,
  onChange,
  onMention,
  teamMembers,
  chatId,
  onSubmit,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Called with (token, userId, displayName) each time a member is selected from the picker. */
  onMention: (token: string, userId: number, displayName: string) => void;
  teamMembers: TeamMember[];
  chatId: number;
  onSubmit: () => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [atPos, setAtPos] = useState<number>(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Include owner row (id === 0, inviteeUserId = null, userId = ownerId) alongside
  // accepted invitee rows so the host can also be tagged in a note.
  const activeMembers = teamMembers.filter(
    (m) => m.status === "accepted" && (m.inviteeUserId != null || m.id === 0),
  );

  const filteredMembers = pickerQuery
    ? activeMembers.filter((m) =>
        searchIncludes(m.name ?? m.email, pickerQuery),
      )
    : activeMembers;

  // Reset keyboard selection when the suggestion list changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [pickerQuery]);

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const val = e.target.value;
    const cursor = e.target.selectionStart ?? val.length;
    onChange(val);
    const textBeforeCursor = val.slice(0, cursor);
    // Only match @-sequences with no spaces (so multi-word names don't split the trigger)
    const match = textBeforeCursor.match(/@([^\s@]*)$/);
    if (match) {
      setShowPicker(true);
      setPickerQuery(match[1]);
      setAtPos(match.index!);
    } else {
      setShowPicker(false);
      setPickerQuery("");
      setAtPos(-1);
    }
  }

  function closePicker() {
    setShowPicker(false);
    setPickerQuery("");
    setAtPos(-1);
    setSelectedIndex(0);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (showPicker && filteredMembers.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, Math.min(filteredMembers.length, 6) - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const idx = Math.min(selectedIndex, filteredMembers.length - 1);
        selectMember(filteredMembers[idx]);
        return;
      }
    }
    if (e.key === "Escape") {
      closePicker();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && value.trim()) {
      e.preventDefault();
      closePicker();
      onSubmit();
    }
  }

  function selectMember(member: TeamMember) {
    // Owner row (id === 0) has userId but no inviteeUserId; use userId as the fallback.
    const resolvedUserId = member.inviteeUserId ?? member.userId;
    if (atPos < 0 || resolvedUserId == null) return;
    const fullName = member.name ?? String(member.email ?? "").split("@")[0];
    // Use first word only so the inserted @Token is space-free and matches the render regex
    const token = fullName.split(/\s+/)[0];
    const before = value.slice(0, atPos);
    const after = value.slice(atPos + 1 + pickerQuery.length);
    onChange(`${before}@${token} ${after}`);
    onMention(token, resolvedUserId, fullName);
    closePicker();
    setTimeout(() => textareaRef.current?.focus(), 0);
  }

  const visibleMembers = filteredMembers.slice(0, 6);

  return (
    <div className="relative flex-1">
      <textarea
        ref={textareaRef}
        placeholder={activeMembers.length > 0 ? "Add a note… (@ to mention)" : "Add an internal note…"}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        rows={1}
        className="flex h-7 w-full resize-none rounded-md border border-input bg-background px-3 py-1 text-xs shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        data-testid={`input-thread-note-${chatId}`}
      />
      {showPicker && visibleMembers.length > 0 && (
        <div
          role="listbox"
          aria-label="Mention a teammate"
          className="absolute bottom-full left-0 z-50 mb-1 w-52 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
        >
          {visibleMembers.map((m, idx) => {
            const displayName = m.name ?? String(m.email ?? "").split("@")[0];
            const isActive = idx === selectedIndex;
            return (
              <button
                key={m.id}
                role="option"
                aria-selected={isActive}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); selectMember(m); }}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-800 ${isActive ? "bg-blue-50" : "hover:bg-gray-50"}`}
                data-testid={`mention-option-${m.id}`}
              >
                <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold text-white ${avatarColor(displayName)}`}>
                  {initials(displayName)}
                </div>
                <span className="truncate" title={displayName}>{displayName}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── Helper: render @mention tokens with purple styling ────────────────────
// When `mentions` (stored user IDs) and `teamMembers` are available, only the
// tokens that correspond to actually-mentioned users are highlighted, preventing
// false positives on arbitrary @words in the note body.
export function renderNoteBody(
  body: string,
  mentions?: number[] | null,
  teamMembers?: TeamMember[],
) {
  // Build the set of first-name tokens that are real mentions
  const mentionedTokens = new Set<string>();
  if (mentions?.length && teamMembers?.length) {
    for (const uid of mentions) {
      // Match against inviteeUserId for team members, or userId for the owner row (id === 0)
      const tm = teamMembers.find((m) => m.inviteeUserId === uid || (m.id === 0 && m.userId === uid));
      if (tm) {
        const fullName = tm.name ?? String(tm.email ?? "").split("@")[0];
        mentionedTokens.add(fullName.split(/\s+/)[0]);
      }
    }
  }
  const hasMentionMeta = mentionedTokens.size > 0;
  const parts = body.split(/(@[^\s@]+)/g);
  return parts.map((part, i) => {
    if (/^@([^\s@]+)/.test(part)) {
      const token = part.slice(1);
      // Only highlight if it matches a real mention (or no metadata available — fallback)
      const isRealMention = hasMentionMeta ? mentionedTokens.has(token) : false;
      if (isRealMention) {
        return <span key={i} className="font-medium text-purple-600">{part}</span>;
      }
    }
    return <span key={i}>{part}</span>;
  });
}


// ─── Chat Bubble with 3-dot menu, workflow pills, and Slack-style threads ─────
// ─── Quo call summary card ────────────────────────────────────────────────────
// Renders a Quo call (metadata.kind === "quo_call") inside the bubble: the
// direction/status/duration line, the AI summary + next-step bullets, any
// voicemail transcript, and a deep link into the Quo conversation.
function QuoCallCard({ call, chatId, tone }: { call: QuoCallView; chatId: number; tone: "dark" | "light" }) {
  const missed = isMissedQuoCall(call);
  const Icon = missed ? PhoneMissed : call.direction === "incoming" ? PhoneIncoming : PhoneOutgoing;
  const duration = formatQuoCallDuration(call.durationSec);
  const muted = tone === "dark" ? "text-blue-100/90" : "text-gray-600";
  const strong = tone === "dark" ? "text-white" : "text-gray-900";
  const link = tone === "dark" ? "text-white underline" : "text-brand underline";
  return (
    <div className="mt-0.5" data-testid={`quo-call-card-${chatId}`}>
      <div className={`flex items-center gap-1.5 text-sm font-medium ${strong}`} data-testid={`quo-call-headline-${chatId}`}>
        <Icon className={`h-4 w-4 ${missed ? "text-red-300" : ""}`} />
        <span>
          {call.direction === "incoming" ? "Incoming call" : "Outgoing call"} · {describeQuoCallStatus(call.direction, call.status)}
          {duration ? ` · ${duration}` : ""}
        </span>
      </div>
      {call.summary.length > 0 && (
        <div className="mt-1.5" data-testid={`quo-call-summary-${chatId}`}>
          <div className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Summary</div>
          <ul className={`mt-0.5 list-disc space-y-0.5 pl-4 text-sm ${strong}`}>
            {call.summary.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {call.nextSteps.length > 0 && (
        <div className="mt-1.5" data-testid={`quo-call-next-steps-${chatId}`}>
          <div className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Next steps</div>
          <ul className={`mt-0.5 list-disc space-y-0.5 pl-4 text-sm ${strong}`}>
            {call.nextSteps.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {call.summary.length === 0 && call.nextSteps.length === 0 && call.summaryStatus === "in-progress" && (
        <div className={`mt-1 text-xs ${muted}`}>Summary is still being generated in Quo.</div>
      )}
      {call.voicemail?.transcript && (
        <div className="mt-1.5" data-testid={`quo-call-voicemail-${chatId}`}>
          <div className={`flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide ${muted}`}>
            <Voicemail className="h-3 w-3" />
            Voicemail{call.voicemail.durationSec ? ` · ${formatQuoCallDuration(call.voicemail.durationSec) ?? ""}` : ""}
          </div>
          <div className={`mt-0.5 whitespace-pre-wrap text-sm ${strong}`}>{call.voicemail.transcript}</div>
          {call.voicemail.recordingUrl && (
            <a
              href={call.voicemail.recordingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`mt-0.5 inline-flex items-center gap-1 text-xs ${link}`}
              data-testid={`quo-call-voicemail-link-${chatId}`}
            >
              Play recording <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
      {call.conversationUrl && (
        <a
          href={call.conversationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${link}`}
          data-testid={`quo-call-open-link-${chatId}`}
        >
          Open in Quo <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

export function ChatBubble({
  chat,
  memberId,
  forceThreadsOpen = false,
  isFocused = false,
  focusThreadId = null,
  teamMembers = [],
  isSearchMatch = false,
  isCurrentMatch = false,
  searchWords = [],
}: {
  chat: TimelineChat;
  memberId: number;
  /** When true, the parent ("Expand all threads" button) forces this bubble's
   * internal-thread panel open regardless of local toggle state. */
  forceThreadsOpen?: boolean;
  /** When true, scroll this bubble into view and briefly highlight it.
   * Used to deep-link from dashboard notifications to the exact message. */
  isFocused?: boolean;
  /** Thread note id to auto-open the panel for + highlight. From ?thread= URL param. */
  focusThreadId?: number | null;
  /** Team members for the @mention picker in thread notes. */
  teamMembers?: TeamMember[];
  /** Task #2296 — true when this bubble's body matches the active inbox search.
   * Keeps a persistent yellow highlight as long as the search is active. */
  isSearchMatch?: boolean;
  /** Task #2296 — true when this bubble is the currently-navigated match.
   * Gets a stronger ring/background to distinguish it from other matches. */
  isCurrentMatch?: boolean;
  /** Task #2297 — case-insensitive words from the active inbox search.
   * When non-empty, the bubble body wraps each occurrence in <mark> so the
   * host can see exactly which text was matched (Ctrl+F style). */
  searchWords?: string[];
}) {
  const queryClient = useQueryClient();
  const { invalidateAfterSingleResolveToggle } = useResolveInvalidations(memberId);
  const isAdmin = chat.senderType === "admin";
  const isPadSplitSource = chat.source === "padsplit";
  // Quo messaging — SMS relayed through the host's Quo inbox (both
  // directions). Admin rows get a "Sent via Quo" pill; a failed/undelivered
  // carrier status surfaces as a red "Not delivered" pill with the code.
  const isQuoSource = chat.source === "quo";
  // Quo call summary rows (message_type='call') render as a call card instead
  // of a text body; the structured payload lives on metadata.
  const quoCall: QuoCallView | null = isQuoSource && chat.messageType === "call" ? parseQuoCallMeta(chat.metadata) : null;
  const quoDeliveryFailed =
    isQuoSource && isAdmin && !quoCall && (chat.deliveryStatus === "failed" || chat.deliveryStatus === "undelivered");
  // True when this delivered admin row was a host's PadPulse compose →
  // warm-browser send (server-derived from a messages_pending pointer). It
  // stays a blue host bubble but gets a "LIVE via PadPulse" pill + the host's
  // own name — distinct from the green "via PS" treatment for messages typed
  // directly inside PadSplit (isPadSplitSource). The two are mutually exclusive
  // server-side (live sends keep source='padpulse', never 'padsplit').
  // Quo rows are checked FIRST: a Quo send also carries a messages_pending
  // pointer, but its channel label is "via Quo", never "LIVE via PadPulse".
  const isPadPulseLive = isAdmin && chat.viaPadPulseLive === true && !isPadSplitSource && !isQuoSource;
  const urls = attachmentUrlsFor(chat);
  const [threadsOpenLocal, setThreadsOpenLocal] = useState(false);
  const meQuery = useQuery<{ id: number; firstName?: string | null; lastName?: string | null; email?: string | null }>({ queryKey: ["/api/app/profile"] });
  const currentUserId = meQuery.data?.id ?? null;
  const currentUserName =
    [meQuery.data?.firstName, meQuery.data?.lastName].filter(Boolean).join(" ").trim() ||
    meQuery.data?.email ||
    null;
  // Auto-open the thread panel only for the bubble that the deep-link points to.
  // `isFocused` is true only when this bubble's chat.id matches the ?chat= URL param,
  // so only that one bubble opens — avoiding unnecessary thread fetches on all others.
  const hasFocusedThread = focusThreadId != null && isFocused;
  const threadsOpen = (forceThreadsOpen && (chat.replyCount ?? 0) > 0) || threadsOpenLocal || hasFocusedThread;
  const setThreadsOpen = setThreadsOpenLocal;
  const { toast } = useToast();
  const [, navigate] = useLocation();

  // Scroll-into-view + temporary highlight when this bubble is the deep-link
  // target. When an inbox search is active the navigator owns scrolling, so
  // the timed-fade only fires for non-search deep links (isFocused && !isSearchMatch).
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [highlight, setHighlight] = useState(false);
  useEffect(() => {
    // Task #2296: if this is a search match, persistent highlighting via
    // isSearchMatch/isCurrentMatch takes over — skip the timed-fade.
    // When hasFocusedThread is true the real target is a specific thread note,
    // not the bubble itself — let the ThreadNote's own scrollIntoView handle it.
    if (!isFocused || isSearchMatch || hasFocusedThread) return;
    const node = bubbleRef.current;
    if (!node) return;

    // Task #2338: Replace the fixed 50ms defer with an rAF-based stability
    // loop. Layout shifts from avatars/images above the bubble and from pages
    // prepended by the deep-link backfill can move the bubble's offsetTop
    // after the initial scroll, landing the view in the wrong position. We
    // poll offsetTop on each animation frame and only fire scrollIntoView once
    // the position has been stable for two consecutive frames. A 3-second wall
    // clock ceiling prevents an infinite loop in pathological cases.
    let cancelled = false;
    let highlightTimer: ReturnType<typeof setTimeout> | null = null;
    let rafId: number | null = null;

    const STABLE_FRAMES_NEEDED = 2;
    const MAX_WAIT_MS = 3000;
    const startedAt = Date.now();
    let lastOffsetTop = -Infinity;
    let stableFrames = 0;

    function doScroll() {
      if (cancelled) return;
      // Task #2340: On mobile (touch devices) and when prefers-reduced-motion
      // is set, iOS Safari's smooth scroll can animate for several seconds
      // after the position stabilises — the user sees the page slide rather
      // than snap, and a new message arriving mid-animation can override the
      // scroll position. Use "instant" in those cases for tighter control.
      const prefersReducedMotion =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const isTouchDevice =
        typeof window !== "undefined" &&
        (("ontouchstart" in window) ||
          (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0));
      const behavior: ScrollBehavior =
        prefersReducedMotion || isTouchDevice ? "instant" : "smooth";
      node!.scrollIntoView({ behavior, block: "center" });
      setHighlight(true);
      highlightTimer = setTimeout(() => {
        if (!cancelled) setHighlight(false);
      }, 2200);
    }

    function checkStability() {
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        doScroll();
        return;
      }
      const currentOffsetTop = node!.offsetTop;
      if (Math.abs(currentOffsetTop - lastOffsetTop) < 1) {
        stableFrames++;
      } else {
        stableFrames = 0;
      }
      lastOffsetTop = currentOffsetTop;
      if (stableFrames >= STABLE_FRAMES_NEEDED) {
        doScroll();
      } else {
        rafId = requestAnimationFrame(checkStability);
      }
    }

    // One initial frame-defer so React has committed sibling bubbles to the
    // DOM and the ScrollArea's scrollHeight reflects the full first page.
    const initialTimer = setTimeout(() => {
      if (!cancelled) rafId = requestAnimationFrame(checkStability);
    }, 50);

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      if (rafId != null) cancelAnimationFrame(rafId);
      if (highlightTimer != null) clearTimeout(highlightTimer);
    };
  }, [isFocused, chat.id, isSearchMatch, hasFocusedThread]);

  // Local workflow state (optimistic overrides on top of server data)
  const [localWorkflow, setLocalWorkflow] = useState<WorkflowFields | null>(chat.workflow ?? null);
  // Sync from props when timeline refreshes
  const prevWorkflowRef = useRef(chat.workflow);
  useEffect(() => {
    if (prevWorkflowRef.current !== chat.workflow) {
      prevWorkflowRef.current = chat.workflow;
      setLocalWorkflow(chat.workflow ?? null);
    }
  }, [chat.workflow]);

  const wf = localWorkflow;

  // ── Due date picker state ──
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [dueDateVal, setDueDateVal] = useState("");
  const [dueTimeVal, setDueTimeVal] = useState("");

  // ── Assignee picker state ──
  const [assigneeOpen, setAssigneeOpen] = useState(false);

  // ── Push-to-maintenance dialog ──
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [maintenanceTitle, setMaintenanceTitle] = useState("");
  const [maintenanceDesc, setMaintenanceDesc] = useState("");
  // Vendor assignment from the message→task flow (Task: auto-select reporter
  // + add vendor in push-to-maintenance). "none" leaves it unassigned so the
  // host can decide later in the task detail sheet.
  const [maintenanceVendorId, setMaintenanceVendorId] = useState<string>("none");
  // Task #1300 follow-up — push-to-maintenance can either spawn a brand-new
  // ticket OR append the chat to an existing open ticket so multiple chats
  // about the same incident roll up into one ticket.
  const [maintenanceMode, setMaintenanceMode] = useState<"new" | "existing">("new");
  const [maintenanceTargetTaskId, setMaintenanceTargetTaskId] = useState<string>("");

  // Vendors for the push-to-maintenance dialog. Only loaded when the dialog
  // opens in "new" mode so we don't pay the round trip on every chat render.
  const vendorsForPushQuery = useQuery<Array<{ id: number; name: string; priority?: number | null; propertyIds?: number[] | null }>>({
    queryKey: ["/api/app/operations/vendors", chat.propertyId ?? null],
    queryFn: async () => {
      const url = chat.propertyId != null
        ? `/api/app/operations/vendors?propertyId=${chat.propertyId}`
        : `/api/app/operations/vendors`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load vendors");
      return res.json();
    },
    enabled: maintenanceOpen && maintenanceMode === "new",
  });
  const pushVendorOptions = (vendorsForPushQuery.data ?? [])
    .filter((v) =>
      chat.propertyId == null
        ? true
        : !v.propertyIds || v.propertyIds.length === 0 || v.propertyIds.includes(chat.propertyId),
    )
    .slice()
    .sort((a, b) => {
      const pa = a.priority ?? Number.MAX_SAFE_INTEGER;
      const pb = b.priority ?? Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      return (a.name || "").localeCompare(b.name || "");
    });

  // Task #1303 — "Link existing ticket" picker. Opens a searchable list of
  // open maintenance tickets and writes the chosen taskId straight to the
  // workflow sidecar (no append/duplication). Surfaces the same green
  // Ticket #N pill on the bubble.
  const [linkTicketOpen, setLinkTicketOpen] = useState(false);
  const [linkTicketSearch, setLinkTicketSearch] = useState("");

  // ── Internal threads ──
  const threadsQuery = useQuery<MemberChatThread[]>({
    queryKey: ["/api/app/member-chats", chat.id, "threads"],
    queryFn: async () => {
      const res = await fetch(`/api/app/member-chats/${chat.id}/threads`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: threadsOpen,
  });

  const [noteBody, setNoteBody] = useState("");
  // Chip-style mention tracking: each confirmed @-pick adds a chip; chips can be
  // dismissed with × (which also strips the @token from the body). Submit uses
  // the chip list directly, so deletions from the text are handled by chip removal.
  const [mentionChips, setMentionChips] = useState<
    Array<{ token: string; userId: number; displayName: string }>
  >([]);

  function handleNoteMention(token: string, userId: number, displayName: string) {
    setMentionChips((prev) =>
      prev.some((c) => c.userId === userId) ? prev : [...prev, { token, userId, displayName }],
    );
  }

  function removeMentionChip(chip: { token: string; userId: number }) {
    setMentionChips((prev) => prev.filter((c) => c.userId !== chip.userId));
    // Also strip the @token from the body (best-effort — handles the common case)
    setNoteBody((prev) => prev.replace(new RegExp(`@${escapeRegExp(chip.token)}\\s?`, "g"), ""));
  }

  const noteMutation = useMutation({
    mutationFn: async () => {
      // Reconcile: only include mentions whose @token still appears in the note
      // body. This handles the case where the user types @Alice then manually
      // deletes "@Alice" from the text without dismissing the chip.
      const presentChips = mentionChips.filter((c) =>
        new RegExp(`@${escapeRegExp(c.token)}(?:[\\s,.]|$)`).test(noteBody),
      );
      const mentions = presentChips.map((c) => c.userId);
      const res = await apiRequest("POST", `/api/app/member-chats/${chat.id}/threads`, {
        body: noteBody.trim(),
        ...(mentions.length > 0 ? { mentions } : {}),
      });
      return res.json();
    },
    onSuccess: () => {
      setNoteBody("");
      setMentionChips([]);
      queryClient.invalidateQueries({ queryKey: ["/api/app/member-chats", chat.id, "threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/notifications"] });
    },
  });

  // ── Workflow mutation (PATCH sidecar) ──
  const workflowMutation = useMutation({
    mutationFn: async (data: Partial<WorkflowFields>) => {
      const res = await apiRequest("PATCH", `/api/app/member-chats/${chat.id}/workflow`, data);
      return res.json();
    },
    onMutate: (data) => {
      setLocalWorkflow((prev) => ({ ...prev, ...data }));
    },
    onSuccess: (updated, data) => {
      setLocalWorkflow(updated);
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      if ("waitingReplyAt" in data) void invalidateWaitingReplyQueries(queryClient, memberId, chat.id);
    },
    onError: (err) => {
      setLocalWorkflow(chat.workflow ?? null);
      toast({ title: "Couldn't update", description: parseErrorMessage(err, "Couldn't update. Please try again."), variant: "destructive" });
    },
  });

  // ── Push-to-maintenance mutation ──
  const pushMaintenanceMutation = useMutation({
    mutationFn: async ({ title, description, vendorId }: { title: string; description?: string; vendorId?: number | null }) => {
      // Auto-carry the chat's member as reporter and its property as the
      // ticket property so the server's reporter+property validation passes
      // (a reporter must be at the task's property) and the host doesn't
      // have to re-pick either field after converting from a chat.
      const body: Record<string, unknown> = {
        title,
        description,
        reporterMemberId: chat.memberId ?? null,
      };
      if (chat.propertyId != null) body.propertyId = chat.propertyId;
      if (vendorId != null) body.vendorId = vendorId;
      const res = await apiRequest("POST", `/api/app/member-chats/${chat.id}/push-to-maintenance`, body);
      return res.json() as Promise<{ maintenanceTaskId: number; taskRef: string }>;
    },
    onSuccess: ({ maintenanceTaskId, taskRef }) => {
      setLocalWorkflow((prev) => ({ ...prev, maintenanceTaskId, maintenanceLinkType: "message" }));
      setMaintenanceOpen(false);
      setMaintenanceTitle("");
      setMaintenanceDesc("");
      setMaintenanceVendorId("none");
      toast({ title: `Linked to ticket ${taskRef}` });
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
    },
    onError: (err) =>
      toast({ title: "Couldn't create ticket", description: parseErrorMessage(err, "Couldn't create ticket. Please try again."), variant: "destructive" }),
  });

  // Task #1300 follow-up — append to existing maintenance task. Calls the
  // sibling endpoint that appends the chat text to the task's description and
  // links the chat workflow to that ticket (so the green "Ticket #N" pill
  // shows up just like with the create-new path).
  const appendMaintenanceMutation = useMutation({
    mutationFn: async (taskId: number) => {
      const res = await apiRequest("POST", `/api/app/member-chats/${chat.id}/append-to-maintenance/${taskId}`);
      return res.json() as Promise<{ maintenanceTaskId: number; taskRef: string }>;
    },
    onSuccess: ({ maintenanceTaskId, taskRef }) => {
      setLocalWorkflow((prev) => ({ ...prev, maintenanceTaskId, maintenanceLinkType: "message_append" }));
      setMaintenanceOpen(false);
      setMaintenanceTargetTaskId("");
      toast({ title: `Appended to ticket ${taskRef}` });
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
    },
    onError: (err) => toast({ title: "Couldn't append to ticket", description: parseErrorMessage(err, "Couldn't append to ticket. Please try again."), variant: "destructive" }),
  });

  // Task #1303 — Link an existing maintenance ticket to this chat WITHOUT
  // duplicating the message text into the ticket. Writes directly to the
  // workflow sidecar so the green "Ticket #N" pill renders on the bubble.
  const linkMaintenanceMutation = useMutation({
    mutationFn: async ({ taskId }: { taskId: number; taskTitle: string }) => {
      // Task #1303 follow-up — server-side endpoint also writes a structured
      // comment ("<host> added info from <member>: \"<text>\"") on the
      // ticket and copies the chat's attachments into the comment so any
      // photos/videos the member sent surface in the ticket thread.
      const res = await apiRequest(
        "POST",
        `/api/app/member-chats/${chat.id}/link-to-maintenance/${taskId}`,
        {},
      );
      return res.json();
    },
    onSuccess: (_data, { taskId, taskTitle }) => {
      // Re-fetch the workflow so the bubble pill updates to the new link.
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId, "comments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId, "linked-chats"] });
      setLocalWorkflow({ ...(localWorkflow ?? {}), maintenanceTaskId: taskId, maintenanceLinkType: "message_link" });
      setLinkTicketOpen(false);
      setLinkTicketSearch("");
      const label = taskTitle || `#${taskId}`;
      toast({
        title: `Message linked to ${label}`,
        duration: 5000,
        action: (
          <ToastAction
            altText="Open task"
            asChild
            data-testid="toast-action-open-linked-task"
          >
            <Link href={`/app/tasks?task=${taskId}`}>Open task</Link>
          </ToastAction>
        ),
      });
    },
    onError: (err) => toast({ title: "Couldn't link ticket", description: parseErrorMessage(err, "Couldn't link ticket. Please try again."), variant: "destructive" }),
  });

  // Task #1528 — Unlink an existing maintenance ticket from this chat. Clears
  // maintenanceTaskId/maintenanceLinkType in the workflow sidecar so the
  // green "Ticket #N" pill disappears from the bubble and the message is
  // removed from the ticket's "Linked messages" section. Confirmation is
  // requested at the call site so accidental unlinks are prevented. The
  // previously-posted ticket comment + attachments are intentionally left
  // in place — the audit trail of what was added is preserved.
  const unlinkMaintenanceMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "DELETE",
        `/api/app/member-chats/${chat.id}/link-to-maintenance`,
      );
      return res.json() as Promise<{ unlinkedTaskId: number | null }>;
    },
    onMutate: () => {
      // Capture the previously-linked ticket so we can invalidate its
      // detail/linked-chats caches after the unlink resolves.
      return { previousTaskId: localWorkflow?.maintenanceTaskId ?? wf?.maintenanceTaskId ?? null };
    },
    onSuccess: (_data, _vars, ctx) => {
      const taskId = ctx?.previousTaskId ?? null;
      setLocalWorkflow({ ...(localWorkflow ?? {}), maintenanceTaskId: null, maintenanceLinkType: null });
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
      if (taskId != null) {
        queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId] });
        queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId, "comments"] });
        queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId, "linked-chats"] });
      }
      toast({ title: "Ticket unlinked" });
    },
    onError: (err) => toast({ title: "Couldn't unlink ticket", description: parseErrorMessage(err, "Couldn't unlink ticket. Please try again."), variant: "destructive" }),
  });

  // Task #2989 — Resolve / unresolve a single inbound member message straight
  // from the bubble. The "Resolved" pill and the menu action both drive this.
  // Optimistic: flip the local resolved state immediately, reconcile on the
  // timeline refetch. Idempotent server-side.
  const [localResolved, setLocalResolved] = useState<{ resolvedAt: string | null; resolvedByName: string | null }>({
    resolvedAt: chat.resolvedAt ? new Date(chat.resolvedAt).toISOString() : null,
    resolvedByName: chat.resolvedByName ?? null,
  });
  const prevResolvedRef = useRef(chat.resolvedAt);
  useEffect(() => {
    if (prevResolvedRef.current !== chat.resolvedAt) {
      prevResolvedRef.current = chat.resolvedAt;
      setLocalResolved({
        resolvedAt: chat.resolvedAt ? new Date(chat.resolvedAt).toISOString() : null,
        resolvedByName: chat.resolvedByName ?? null,
      });
    }
  }, [chat.resolvedAt, chat.resolvedByName]);

  const resolveMutation = useMutation({
    mutationFn: async (resolve: boolean) => {
      const action = resolve ? "resolve" : "unresolve";
      const res = await apiRequest("POST", `/api/app/messages/${chat.id}/${action}`);
      return res.json();
    },
    onMutate: async (resolve: boolean) => {
      const prev = localResolved;
      setLocalResolved(resolve ? { resolvedAt: new Date().toISOString(), resolvedByName: currentUserName } : { resolvedAt: null, resolvedByName: null });
      // Task #4166 — move the conversation-list "N open" badge instantly: a
      // resolve drops this member's open count by one; an unresolve bumps it
      // back, restoring the oldest-open age hint from this bubble's timestamp.
      const { prevInbox } = await applyOptimisticInboxResolve(
        queryClient,
        memberId,
        resolve
          ? decrementInboxRowUpdate
          : incrementInboxRowUpdate(1, chat.createdAt ? new Date(chat.createdAt).toISOString() : null),
      );
      return { prev, prevInbox };
    },
    onSuccess: () => {
      invalidateAfterSingleResolveToggle();
    },
    onError: (err, _resolve, ctx) => {
      if (ctx?.prev) setLocalResolved(ctx.prev);
      rollbackOptimisticInboxResolve(queryClient, ctx?.prevInbox);
      toast({ title: "Couldn't update resolution", description: parseErrorMessage(err, "Couldn't update resolution. Please try again."), variant: "destructive" });
    },
  });

  const isResolved = localResolved.resolvedAt != null;

  const [unlinkTaskConfirmId, setUnlinkTaskConfirmId] = useState<number | null>(null);

  function confirmUnlinkMaintenance() {
    const taskId = wf?.maintenanceTaskId;
    if (!taskId) return;
    setUnlinkTaskConfirmId(taskId);
  }

  // Open maintenance tasks for the "Add to existing ticket" picker. Only
  // queried when the user actually flips the dialog into "existing" mode so
  // we don't load the full task list on every chat row.
  const openTasksQuery = useQuery<Array<{ id: number; title: string; status: string; propertyId: number | null }>>({
    queryKey: ["/api/app/tasks"],
    enabled: (maintenanceOpen && maintenanceMode === "existing") || linkTicketOpen,
  });
  // Property list so we can render "<Property Name> — <Title> (#N)" in the
  // existing-ticket picker. Without this the dropdown previously showed only
  // ticket numbers + titles, which made it hard to pick the right ticket
  // when several open tickets had similar names.
  const propertiesForPicker = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
    enabled: (maintenanceOpen && maintenanceMode === "existing") || linkTicketOpen,
  });
  const propNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of propertiesForPicker.data ?? []) m.set(p.id, p.name);
    return m;
  }, [propertiesForPicker.data]);
  const openTasks = (openTasksQuery.data ?? []).filter((t) => t.status !== "completed");
  // Prefer same-property tasks at the top so "this leak again" tickets are
  // surfaced first; fall back to the rest of the host's open tasks below.
  const sameProp = chat.propertyId != null ? openTasks.filter((t) => t.propertyId === chat.propertyId) : [];
  const otherProp = openTasks.filter((t) => !sameProp.includes(t));
  const tasksOrdered = [...sameProp, ...otherProp];

  function openMaintenanceDialog() {
    setMaintenanceTitle(chat.messageText?.slice(0, 80) ?? "");
    setMaintenanceDesc(chat.messageText ?? "");
    setMaintenanceOpen(true);
  }

  function handleDueDateConfirm() {
    if (!dueDateVal) return;
    const iso = `${dueDateVal}T${dueTimeVal || "00:00"}`;
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return;
    workflowMutation.mutate({ dueDate: dt.toISOString() });
    setDueDateOpen(false);
  }

  // Derive reply count from threadsQuery if open, else from the timeline prop
  const replyCount = threadsOpen ? (threadsQuery.data?.length ?? chat.replyCount ?? 0) : (chat.replyCount ?? 0);
  const lastReplyAuthor = chat.lastReplyAuthorName;
  const lastReplyAt = chat.lastReplyAt;

  // PadSplit send-UX — optimistic bubble. Paints a calm "Sent" the instant the
  // operator hits Send, decoupled from the PadSplit push round-trip (mirroring
  // how PadSplit hides its own latency). The persisted `messages_pending` row
  // takes over on the next timeline refetch. The "failed" state here is a rare
  // fallback for a genuine REQUEST error (network / validation / outage) — the
  // PadSplit push itself no longer hard-errors on the hot path, so a slow or
  // failing push never flips this bubble; terminal failure routes via the bell
  // + Team Inbox instead. We intentionally render no Retry affordance here.
  if (chat._optimistic) {
    const isFailed = chat._optimisticStatus === "failed";
    return (
      <div className="mb-1 flex flex-col items-end">
        <div
          className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${isFailed ? "bg-red-100 border border-red-300 text-red-800" : "bg-blue-600 text-white border border-blue-400"}`}
          data-testid={`bubble-optimistic-${chat.id}`}
        >
          <div className={`mb-1 flex items-center gap-1.5 text-[11px] ${isFailed ? "text-red-700" : "text-blue-100"}`}>
            {isFailed ? (
              <AlertCircle className="h-3 w-3" />
            ) : (
              <CheckCircle className="h-3 w-3" />
            )}
            <span>{isFailed ? "Couldn't send — refreshing…" : "Sent"}</span>
          </div>
          {chat.messageText ? <MessageBody text={urls.length > 0 ? stripMediaUrls(chat.messageText) : chat.messageText} /> : null}
        </div>
      </div>
    );
  }

  // Task #1300 follow-up — Pending bubble rendering. Renders queued
  // messages_pending rows (sending / scheduled / awaiting) inline with
  // cancel + reschedule actions so hosts can manage outbounds without
  // leaving the chat thread.
  if (chat.pending) {
    // PadSplit send-UX — when this pending row is the deep-link target
    // (?pending=P from a failed-send surface), attach the shared scroll ref +
    // timed highlight so the existing isFocused scroll effect lands on and
    // briefly rings the exact failed bubble.
    return (
      <div
        ref={bubbleRef}
        className={highlight ? "rounded-lg ring-2 ring-yellow-400 ring-offset-2 transition-shadow" : ""}
        data-testid={`bubble-pending-focus-${chat.pending.id}`}
      >
        {chat.pending.autoDraft && (chat.pending.status === "scheduled" || chat.pending.status === "awaiting")
          ? <AutoDraftBubble chat={chat} memberId={memberId} />
          : <PendingChatBubble chat={chat} memberId={memberId} />}
      </div>
    );
  }

  // Task #2296 — Compute the highlight class for this bubble.
  // Priority: current match (strong) > any match (light) > timed deep-link fade.
  const highlightClass = isCurrentMatch
    ? "bg-yellow-200/90 ring-2 ring-yellow-500 p-1"
    : isSearchMatch
      ? "bg-yellow-100/70 ring-1 ring-yellow-400 p-1"
      : highlight
        ? "bg-yellow-100/70 ring-2 ring-yellow-400 p-1"
        : "";

  return (
    <div
      ref={bubbleRef}
      className={`group mb-1 flex flex-col rounded-lg transition-all duration-300 ${isAdmin ? "items-end" : "items-start"} ${highlightClass}`}
      data-testid={`bubble-${chat.id}`}
    >
      <div className="relative flex items-start gap-1">
        {/* 3-dot menu — always on admin side, appears on hover for member bubbles */}
        {!isAdmin && (
          <div className="flex shrink-0 items-center opacity-0 group-hover:opacity-100 transition-opacity mt-1">
            <BubbleActionsMenu
              chat={chat}
              isAwaitingReply={!!wf?.waitingReplyAt}
              isResolved={isResolved}
              onResolve={() => resolveMutation.mutate(true)}
              onUnresolve={() => resolveMutation.mutate(false)}
              onPushToMaintenance={openMaintenanceDialog}
              onLinkExistingTicket={() => setLinkTicketOpen(true)}
              linkedMaintenanceTaskId={wf?.maintenanceTaskId ?? null}
              onUnlinkMaintenance={confirmUnlinkMaintenance}
              onSetDueDate={() => {
                setDueDateVal(wf?.dueDate ? new Date(wf.dueDate).toISOString().slice(0, 10) : "");
                setDueTimeVal(wf?.dueDate ? new Date(wf.dueDate).toTimeString().slice(0, 5) : "");
                setDueDateOpen(true);
              }}
              onAssign={() => setAssigneeOpen(true)}
              onStartThread={() => setThreadsOpen(true)}
              // No awaiting-reply on inbound bubbles — host can't be
              // "awaiting" a reply to a member's own message.
            />
          </div>
        )}
        <div
          className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
            wf?.isBroadcast
              // Broadcast bubbles render pink so a one-to-many announcement
              // is instantly distinguishable from a normal 1:1 admin reply.
              ? "bg-pink-500 text-white border border-pink-400"
              : isAdmin
                // All host/admin bubbles (PadPulse-sent and PadSplit-sourced)
                // are the same solid blue. PadSplit-sourced ones are
                // distinguished only by the "Sent via PadSplit" badge below.
                ? "bg-blue-600 text-white"
                // Inbound member bubbles: unresolved messages get a very faint
                // brand-orange tint (10% opacity) so open items stand out at a
                // glance; resolving one turns it plain white. (Task: message
                // resolution color cue.)
                : isResolved
                  ? "bg-white text-gray-900 border border-gray-200"
                  : "bg-brand-accent/10 text-gray-900 border border-brand-accent/30"
          }`}
          data-testid={`bubble-chat-${chat.id}`}
          data-broadcast={wf?.isBroadcast ? "true" : undefined}
          data-source={isPadSplitSource ? "padsplit" : isQuoSource ? "quo" : undefined}
          data-via-padpulse-live={isPadPulseLive ? "true" : undefined}
        >
          <div className={`mb-1 flex items-center gap-1.5 text-[11px] ${
            wf?.isBroadcast
              ? "text-pink-100/90 dark:text-pink-100/90"
              : isAdmin
                ? "text-blue-100"
                : "text-gray-500"
          }`}>
            <span>
              {isPadSplitSource && isAdmin
                ? `${chat.senderName ?? "Admin"} (via PadSplit)`
                : isQuoSource && isAdmin
                  ? `${chat.senderName ?? "Admin"} (via Quo)`
                  : chat.senderName ?? (isAdmin ? "Admin" : "Member")}
              {" · "}{formatTime(chat.createdAt)}
            </span>
            {isQuoSource && isAdmin && !quoDeliveryFailed && !quoCall && (
              <span
                className="inline-flex items-center rounded bg-white px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide text-blue-700 dark:bg-white/15 dark:text-white"
                data-testid={`pill-quo-source-${chat.id}`}
              >
                Sent via Quo
              </span>
            )}
            {quoCall && (
              <span
                className={`inline-flex items-center gap-0.5 rounded px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide ${
                  isAdmin ? "bg-white text-blue-700 dark:bg-white/15 dark:text-white" : "bg-blue-600 text-white"
                }`}
                data-testid={`pill-quo-call-${chat.id}`}
              >
                Quo call summary
              </span>
            )}
            {quoDeliveryFailed && (
              <span
                className="inline-flex items-center gap-0.5 rounded bg-red-600 px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide text-white"
                title={chat.deliveryError ? `Carrier error ${chat.deliveryError}` : "Quo reported the text was not delivered"}
                data-testid={`pill-quo-undelivered-${chat.id}`}
              >
                <AlertCircle className="h-2.5 w-2.5" />
                Not delivered{chat.deliveryError ? ` · ${chat.deliveryError}` : ""}
              </span>
            )}
            {isPadSplitSource && isAdmin && (
              // White pill with blue text — matches the style of the old
              // "LIVE via PadPulse" badge so the channel indicator reads
              // crisply on the blue bubble without adding a second color.
              <span
                className="inline-flex items-center rounded bg-white px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide text-blue-700 dark:bg-white/15 dark:text-white"
                data-testid={`pill-padsplit-source-${chat.id}`}
              >
                Sent via PadSplit
              </span>
            )}
            {/* isPadPulseLive pill removed — sending via PadPulse is the
                default; the badge was noise. The data-via-padpulse-live
                attribute is retained on the bubble div for diagnostics. */}
            {wf?.isBroadcast && (
              <span className="inline-flex items-center gap-0.5 rounded bg-white/20 px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide" data-testid={`pill-broadcast-${chat.id}`}>
                <Megaphone className="h-2.5 w-2.5" />
                Broadcast
              </span>
            )}
            {wf?.waitingReplyAt && isAdmin && (
              // Awaiting-reply pill is admin-only and rendered in the
              // PadSplit gold/yellow palette so it stands out from the
              // other workflow pills (assignee=purple, due=orange,
              // ticket=green) and from the bubble's own colors.
              <span
                className="inline-flex items-center gap-1 rounded-full bg-yellow-400 px-2 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-yellow-900 ring-1 ring-yellow-500 shadow-sm"
                title={`Awaiting reply since ${new Date(wf.waitingReplyAt).toLocaleString()}`}
                data-testid={`pill-waiting-reply-${chat.id}`}
              >
                <Clock className="h-3 w-3" />
                Awaiting reply
              </span>
            )}
            {/* Task #4266 — "Sent by …" automation attribution chip. Appears
                only on admin bubbles that were generated by an automated
                process (messaging rule or low-battery cron). Clickable:
                low_battery → deep-links to the Low Battery Auto-Notification
                settings block; rule → deep-links to the rule editor. Hand-
                typed host messages have automationAttribution=null/undefined. */}
            {isAdmin && chat.automationAttribution && (
              <AttributionChip
                attribution={chat.automationAttribution}
                chatId={chat.id}
              />
            )}
            {/* Task #2989 / #3134 — Resolution pill on inbound member bubbles.
                Shows a green checkmark plus the resolver's name ("Resolved by
                <name>") so accountability is visible inline on shared inboxes,
                not just on hover. Falls back to a plain "Resolved" label for
                legacy rows whose resolved_by is null. The tooltip still carries
                the resolution timestamp. Inbound-only because the resolution
                signal is stamped on member→host messages. */}
            {!isAdmin && isResolved && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium leading-none text-gray-400 dark:text-gray-500"
                title={localResolved.resolvedByName ? `Resolved by ${localResolved.resolvedByName}` : "Resolved"}
                data-testid={`pill-resolved-${chat.id}`}
              >
                <Check className="h-3 w-3" />
                {localResolved.resolvedAt ? `Resolved at ${new Date(localResolved.resolvedAt).toLocaleString()}` : "Resolved"}
              </span>
            )}
          </div>
          {quoCall ? (
            <QuoCallCard call={quoCall} chatId={chat.id} tone={isAdmin ? "dark" : "light"} />
          ) : chat.messageText ? (
            <MessageBody
              text={urls.length > 0 ? stripMediaUrls(chat.messageText) : chat.messageText}
              highlightWords={isSearchMatch ? searchWords : undefined}
            />
          ) : null}
          {urls.length > 0 ? <MessageAttachments urls={urls} messageId={chat.id} tone={isAdmin ? "dark" : "light"} /> : null}
          {/* Workflow pills */}
          {(wf?.dueDate || wf?.assigneeName || wf?.maintenanceTaskId) && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {wf.dueDate && (
                <button
                  onClick={() => {
                    setDueDateVal(new Date(wf.dueDate!).toISOString().slice(0, 10));
                    setDueTimeVal(new Date(wf.dueDate!).toTimeString().slice(0, 5));
                    setDueDateOpen(true);
                  }}
                  className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${isAdmin ? "bg-blue-500 text-blue-100 hover:bg-blue-400" : "bg-orange-100 text-orange-700 hover:bg-orange-200"}`}
                  data-testid={`pill-due-date-${chat.id}`}
                >
                  <Calendar className="h-2.5 w-2.5" />
                  Due {new Date(wf.dueDate).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </button>
              )}
              {wf.assigneeName && (
                <button
                  onClick={() => setAssigneeOpen(true)}
                  className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${isAdmin ? "bg-blue-500 text-blue-100 hover:bg-blue-400" : "bg-purple-100 text-purple-700 hover:bg-purple-200"}`}
                  data-testid={`pill-assignee-${chat.id}`}
                >
                  <UserCheck className="h-2.5 w-2.5" />
                  {wf.assigneeName}
                </button>
              )}
              {wf.maintenanceTaskId && (
                // Task #1528 — pill now wraps the link + an "x" unlink button
                // so hosts have a one-click way to sever the link straight
                // from the bubble (with confirmation). The link half still
                // navigates to the ticket; the "x" half clears the workflow
                // sidecar after a confirm prompt.
                <span
                  className={`inline-flex items-stretch overflow-hidden rounded text-[10px] font-medium ${isAdmin ? "bg-blue-500 text-blue-100" : "bg-green-100 text-green-700"}`}
                  data-testid={`pill-maintenance-link-${chat.id}`}
                >
                  <a
                    href={`/app/tasks?task=${wf.maintenanceTaskId}`}
                    onMouseEnter={() => {
                      // Warm up the single-task cache so the detail sheet
                      // pops open instantly when the user clicks the pill.
                      // The maintenance page reads `["/api/app/tasks", id]`
                      // for the task detail and `["/api/app/tasks", id, "comments"]`
                      // for comments — prefetch both.
                      queryClient.prefetchQuery({
                        queryKey: ["/api/app/tasks", wf.maintenanceTaskId],
                      });
                      queryClient.prefetchQuery({
                        queryKey: ["/api/app/tasks", wf.maintenanceTaskId, "comments"],
                      });
                    }}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 ${isAdmin ? "hover:bg-blue-400" : "hover:bg-green-200"}`}
                  >
                    <Wrench className="h-2.5 w-2.5" />
                    Ticket #{wf.maintenanceTaskId}
                  </a>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); confirmUnlinkMaintenance(); }}
                    disabled={unlinkMaintenanceMutation.isPending}
                    title="Remove link to ticket"
                    aria-label={`Remove link to ticket #${wf.maintenanceTaskId}`}
                    className={`inline-flex items-center px-1 border-l ${isAdmin ? "border-blue-400 hover:bg-blue-400" : "border-green-200 hover:bg-green-200"} disabled:opacity-50`}
                    data-testid={`button-unlink-maintenance-${chat.id}`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
        {isAdmin && (
          <div className="flex shrink-0 items-center opacity-0 group-hover:opacity-100 transition-opacity mt-1">
            <BubbleActionsMenu
              chat={chat}
              isAwaitingReply={!!wf?.waitingReplyAt}
              onPushToMaintenance={openMaintenanceDialog}
              onLinkExistingTicket={() => setLinkTicketOpen(true)}
              linkedMaintenanceTaskId={wf?.maintenanceTaskId ?? null}
              onUnlinkMaintenance={confirmUnlinkMaintenance}
              onSetDueDate={() => {
                setDueDateVal(wf?.dueDate ? new Date(wf.dueDate).toISOString().slice(0, 10) : "");
                setDueTimeVal(wf?.dueDate ? new Date(wf.dueDate).toTimeString().slice(0, 5) : "");
                setDueDateOpen(true);
              }}
              onAssign={() => setAssigneeOpen(true)}
              onStartThread={() => setThreadsOpen(true)}
              onToggleAwaitingReply={() => workflowMutation.mutate({ waitingReplyAt: wf?.waitingReplyAt ? null : new Date().toISOString() })}
            />
          </div>
        )}
      </div>

      {/* Inline "Resolve" — inbound member bubbles only, shown ONLY while the
          message is still open, pinned to bottom-right. Once resolved the inline
          control disappears (the gray "Resolved at …" indicator takes its place);
          re-opening a resolved message lives exclusively in the 3-dot menu so a
          resolved bubble can't be accidentally re-opened with a single click.
          self-stretch overrides the parent's items-start so this row fills the full
          bubble width, giving justify-end the space to anchor the button right. */}
      {!isAdmin && !isResolved && (
        <div className="self-stretch flex justify-end">
          <button
            type="button"
            onClick={() => resolveMutation.mutate(true)}
            disabled={resolveMutation.isPending}
            className="mt-0.5 flex items-center gap-1 text-[11px] text-brand hover:text-brand-hover transition-colors disabled:opacity-50"
            data-testid={`button-inline-resolve-${chat.id}`}
          >
            <Check className="h-3 w-3" />
            Resolve
          </button>
        </div>
      )}

      {/* Slack-style reply affordance */}
      {replyCount > 0 ? (
        <button
          onClick={() => setThreadsOpen(!threadsOpen)}
          className="mt-0.5 flex items-center gap-1 text-[11px] text-blue-500 hover:text-blue-700"
          data-testid={`button-toggle-threads-${chat.id}`}
        >
          <MessageCircle className="h-3 w-3" />
          {replyCount} {replyCount === 1 ? "reply" : "replies"}
          {lastReplyAuthor && <span className="text-gray-400"> · last by {lastReplyAuthor}{lastReplyAt ? `, ${formatTime(lastReplyAt)}` : ""}</span>}
          {threadsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      ) : (
        <button
          onClick={() => setThreadsOpen(!threadsOpen)}
          className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
          data-testid={`button-toggle-threads-${chat.id}`}
        >
          {threadsOpen ? (
            <>
              <ChevronUp className="h-3 w-3" /> Hide notes
            </>
          ) : (
            <>
              <Plus className="h-2.5 w-2.5" /> Add internal note
            </>
          )}
        </button>
      )}

      {threadsOpen && (
        <div className="mt-1 w-full max-w-lg rounded-md border border-gray-200 bg-gray-50 p-2" data-testid={`threads-panel-${chat.id}`}>
          {threadsQuery.isLoading ? (
            <div className="text-xs text-gray-400 py-2">Loading notes…</div>
          ) : (threadsQuery.data ?? []).length === 0 ? (
            <div className="text-xs text-gray-400 py-1">No internal notes yet.</div>
          ) : (
            <div className="space-y-1.5 mb-2">
              {(threadsQuery.data ?? []).map((t) => (
                <ThreadNote
                  key={t.id}
                  note={t}
                  isFocused={focusThreadId === t.id}
                  teamMembers={teamMembers ?? []}
                  currentUserId={currentUserId}
                  chatId={chat.id}
                  memberId={memberId}
                />
              ))}
            </div>
          )}
          <div className="flex gap-1">
            <MentionNoteInput
              value={noteBody}
              onChange={setNoteBody}
              onMention={handleNoteMention}
              teamMembers={teamMembers ?? []}
              chatId={chat.id}
              onSubmit={() => { if (noteBody.trim()) noteMutation.mutate(); }}
            />
            <Button
              size="sm"
              className="h-7 px-2"
              disabled={!noteBody.trim() || noteMutation.isPending}
              onClick={() => noteMutation.mutate()}
              data-testid={`button-submit-thread-${chat.id}`}
            >
              {noteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            </Button>
          </div>
          {mentionChips.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {mentionChips.map((chip) => (
                <span
                  key={chip.userId}
                  className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700"
                  data-testid={`mention-chip-${chip.userId}`}
                >
                  @{chip.displayName}
                  <button
                    type="button"
                    onClick={() => removeMentionChip(chip)}
                    className="ml-0.5 text-purple-400 hover:text-purple-700"
                    aria-label={`Remove mention of ${chip.displayName}`}
                    data-testid={`remove-mention-${chip.userId}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          {teamMembers && teamMembers.filter((m) => m.status === "accepted").length > 0 && mentionChips.length === 0 && (
            <div className="mt-1 flex items-center gap-1 text-[10px] text-gray-400">
              <AtSign className="h-2.5 w-2.5" />
              <span>Type @ to mention a teammate</span>
            </div>
          )}
        </div>
      )}

      {/* Due date picker popover */}
      <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
        <PopoverTrigger className="hidden" />
        <PopoverContent className="w-64 p-3" side="top">
          <div className="mb-2 text-sm font-medium">Set due date</div>
          <Input type="date" value={dueDateVal} onChange={(e) => setDueDateVal(e.target.value)} className="mb-2" />
          <Input type="time" value={dueTimeVal} onChange={(e) => setDueTimeVal(e.target.value)} className="mb-3" />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setDueDateOpen(false)}>Cancel</Button>
            {wf?.dueDate && (
              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { workflowMutation.mutate({ dueDate: null }); setDueDateOpen(false); }}>
                Clear
              </Button>
            )}
            <Button size="sm" onClick={handleDueDateConfirm} disabled={workflowMutation.isPending}>Set</Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Assignee picker popover */}
      <AssigneePicker
        open={assigneeOpen}
        onOpenChange={setAssigneeOpen}
        currentAssigneeId={wf?.assigneeId ?? null}
        currentAssigneeName={wf?.assigneeName ?? null}
        onAssign={(id, name) => {
          workflowMutation.mutate({ assigneeId: id, assigneeName: name, assignedAt: new Date().toISOString() });
        }}
        onClear={() => {
          workflowMutation.mutate({ assigneeId: null, assigneeName: null, assignedAt: null });
        }}
        chatId={chat.id}
      />

      {/* Push to maintenance dialog */}
      <Dialog open={maintenanceOpen} onOpenChange={setMaintenanceOpen}>
        <DialogContent className="max-w-md" data-testid={`dialog-push-maintenance-${chat.id}`}>
          <DialogHeader>
            <DialogTitle>Push to maintenance ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Mode toggle — pick "create new" (default) vs append to an
                existing open ticket. Defaults to "new" so the most common
                path stays one-click. */}
            <RadioGroup
              value={maintenanceMode}
              onValueChange={(v) => setMaintenanceMode(v as "new" | "existing")}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="new" id={`maint-mode-new-${chat.id}`} data-testid={`radio-maintenance-new-${chat.id}`} />
                <Label htmlFor={`maint-mode-new-${chat.id}`} className="text-sm cursor-pointer">Create new ticket</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="existing" id={`maint-mode-existing-${chat.id}`} data-testid={`radio-maintenance-existing-${chat.id}`} />
                <Label htmlFor={`maint-mode-existing-${chat.id}`} className="text-sm cursor-pointer">Add to existing ticket</Label>
              </div>
            </RadioGroup>
            {maintenanceMode === "new" ? (
              <>
                <div>
                  <Label className="text-sm">Title</Label>
                  <Input
                    value={maintenanceTitle}
                    onChange={(e) => setMaintenanceTitle(e.target.value)}
                    className="mt-1"
                    data-testid={`input-maintenance-title-${chat.id}`}
                  />
                </div>
                <div>
                  <Label className="text-sm">Description</Label>
                  <Textarea
                    value={maintenanceDesc}
                    onChange={(e) => setMaintenanceDesc(e.target.value)}
                    className="mt-1 min-h-[80px]"
                    data-testid={`input-maintenance-desc-${chat.id}`}
                  />
                </div>
                <div>
                  <Label className="text-sm">Assign vendor (optional)</Label>
                  <Select value={maintenanceVendorId} onValueChange={setMaintenanceVendorId}>
                    <SelectTrigger className="mt-1" data-testid={`select-maintenance-vendor-${chat.id}`}>
                      <SelectValue placeholder="No vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" data-testid="option-maintenance-vendor-none">No vendor</SelectItem>
                      {pushVendorOptions.map((v) => (
                        <SelectItem key={v.id} value={String(v.id)} data-testid={`option-maintenance-vendor-${v.id}`}>
                          {v.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="mt-1 text-[11px] text-gray-500">
                    The vendor will see the ticket in their portal and you can chat with them in the ticket's Comments tab.
                  </div>
                </div>
                <div className="text-[11px] text-gray-500">
                  Reporter: <span className="font-medium text-gray-700">{chat.senderName || "this member"}</span>
                  {chat.propertyId == null && (
                    <span className="ml-1 text-amber-600">(no property on this chat — reporter will be left unset)</span>
                  )}
                </div>
              </>
            ) : (
              <div>
                <Label className="text-sm">Existing open ticket</Label>
                {openTasksQuery.isLoading ? (
                  <div className="mt-1 text-xs text-gray-400">Loading tickets…</div>
                ) : tasksOrdered.length === 0 ? (
                  <div className="mt-1 text-xs text-gray-400">No open tickets.</div>
                ) : (
                  <Select value={maintenanceTargetTaskId} onValueChange={setMaintenanceTargetTaskId}>
                    <SelectTrigger className="mt-1" data-testid={`select-maintenance-target-${chat.id}`}>
                      <SelectValue placeholder="Pick a ticket…" />
                    </SelectTrigger>
                    <SelectContent>
                      {tasksOrdered.map((t) => {
                        const propName = t.propertyId != null ? propNameById.get(t.propertyId) : null;
                        const sameProperty = chat.propertyId != null && t.propertyId === chat.propertyId;
                        // Render "<Property> — <Title> (#N)" so a host
                        // glancing at the dropdown can pick the right
                        // ticket by property + subject, not by an opaque
                        // task number.
                        const left = propName ? `${propName} — ${t.title}` : t.title;
                        return (
                          <SelectItem key={t.id} value={String(t.id)} data-testid={`option-maintenance-task-${t.id}`}>
                            {left} (#{t.id}){sameProperty ? " · same property" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
                <div className="mt-2 text-[11px] text-gray-500">
                  The chat message will be appended to the ticket's description with a timestamp.
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaintenanceOpen(false)}>Cancel</Button>
            {maintenanceMode === "new" ? (
              <Button
                onClick={() => pushMaintenanceMutation.mutate({
                  title: maintenanceTitle,
                  description: maintenanceDesc,
                  vendorId: maintenanceVendorId === "none" ? null : Number(maintenanceVendorId),
                })}
                disabled={!maintenanceTitle.trim() || pushMaintenanceMutation.isPending}
                data-testid={`button-push-maintenance-${chat.id}`}
              >
                {pushMaintenanceMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wrench className="h-4 w-4 mr-1" />}
                Create ticket
              </Button>
            ) : (
              <Button
                onClick={() => maintenanceTargetTaskId && appendMaintenanceMutation.mutate(Number(maintenanceTargetTaskId))}
                disabled={!maintenanceTargetTaskId || appendMaintenanceMutation.isPending}
                data-testid={`button-append-maintenance-${chat.id}`}
              >
                {appendMaintenanceMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wrench className="h-4 w-4 mr-1" />}
                Append to ticket
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #1303 — Link existing ticket dialog. Searchable list of open
          maintenance tickets, with same-property tickets surfaced first. On
          select we just write maintenanceTaskId to the workflow sidecar (no
          ticket creation, no message duplication) and the green Ticket #N
          pill renders on the bubble via the existing pill logic above. */}
      <Dialog open={linkTicketOpen} onOpenChange={(v) => { setLinkTicketOpen(v); if (!v) setLinkTicketSearch(""); }}>
        <DialogContent className="max-w-md" data-testid={`dialog-link-ticket-${chat.id}`}>
          <DialogHeader>
            <DialogTitle>Link existing maintenance ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search open tickets…"
              value={linkTicketSearch}
              onChange={(e) => setLinkTicketSearch(e.target.value)}
              autoFocus
              data-testid={`input-link-ticket-search-${chat.id}`}
            />
            <div className="max-h-72 overflow-y-auto -mx-1 px-1">
              {openTasksQuery.isLoading ? (
                <div className="py-3 text-xs text-gray-400">Loading tickets…</div>
              ) : tasksOrdered.length === 0 ? (
                <div className="py-3 text-xs text-gray-400">No open tickets.</div>
              ) : chat.propertyId != null && tasksOrdered.filter((t) => t.propertyId === chat.propertyId).length === 0 ? (
                <div className="py-3 text-xs text-gray-400">No open tickets for this property.</div>
              ) : (
                (() => {
                  // Task #1303 — when the chat is associated with a property,
                  // strictly scope the picker to that property's open tickets
                  // (per spec: "open maintenance tasks for the property"). For
                  // chats without a property we fall back to all open tickets
                  // so hosts aren't blocked.
                  const scoped = chat.propertyId != null
                    ? tasksOrdered.filter((t) => t.propertyId === chat.propertyId)
                    : tasksOrdered;
                  const q = linkTicketSearch.trim().toLowerCase();
                  const filtered = q
                    ? scoped.filter((t) => {
                        const propName = t.propertyId != null ? propNameById.get(t.propertyId) ?? "" : "";
                        return (
                          String(t.title ?? "").toLowerCase().includes(q) ||
                          propName.toLowerCase().includes(q) ||
                          String(t.id).includes(q)
                        );
                      })
                    : scoped;
                  if (filtered.length === 0) {
                    return <div className="py-3 text-xs text-gray-400">No tickets match "{linkTicketSearch}".</div>;
                  }
                  return (
                    <div className="space-y-0.5">
                      {filtered.map((t) => {
                        const propName = t.propertyId != null ? propNameById.get(t.propertyId) : null;
                        const sameProperty = chat.propertyId != null && t.propertyId === chat.propertyId;
                        const isCurrent = wf?.maintenanceTaskId === t.id;
                        return (
                          <button
                            key={t.id}
                            disabled={linkMaintenanceMutation.isPending}
                            onClick={() => linkMaintenanceMutation.mutate({ taskId: t.id, taskTitle: t.title })}
                            className={`w-full text-left rounded px-2 py-1.5 text-sm hover:bg-gray-100 disabled:opacity-50 ${isCurrent ? "bg-blue-50 text-blue-700" : ""}`}
                            data-testid={`button-link-ticket-${t.id}`}
                          >
                            <div className="flex items-center gap-1.5">
                              <Wrench className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                              <span className="font-medium truncate">{t.title}</span>
                              <span className="ml-auto text-[11px] text-gray-400 shrink-0">#{t.id}</span>
                            </div>
                            <div className="mt-0.5 pl-5 text-[11px] text-gray-500">
                              {propName ?? "No property"}
                              {sameProperty ? <span className="ml-1 text-blue-600">· same property</span> : null}
                              {isCurrent ? <span className="ml-1 text-blue-600">· currently linked</span> : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </div>
            <div className="text-[11px] text-gray-500">
              The chat will be linked to the selected ticket. The ticket's contents won't be modified.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkTicketOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={unlinkTaskConfirmId !== null} onOpenChange={(v) => !v && setUnlinkTaskConfirmId(null)}>
        <AlertDialogContent data-testid="dialog-unlink-ticket-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {unlinkTaskConfirmId !== null ? `Remove the link to ticket #${unlinkTaskConfirmId}?` : "Remove the link to this ticket?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              The message will no longer appear under the ticket's linked messages. The ticket's existing comments will not be changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-unlink-ticket">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-unlink-ticket"
              disabled={unlinkMaintenanceMutation.isPending}
              onClick={() => {
                unlinkMaintenanceMutation.mutate(undefined, {
                  onSettled: () => setUnlinkTaskConfirmId(null),
                });
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {unlinkMaintenanceMutation.isPending ? "Unlinking..." : "Unlink"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// ─── Pending Chat Bubble (Task #1300 follow-up) ──────────────────────────────
// Renders an undelivered messages_pending row inline in the timeline so the
// host can see what's queued/scheduled/awaiting and cancel or reschedule it
// without leaving the chat thread. The synthesized chat row carries a
// `pending` marker plus the original messages_pending id; the marker drives
// the badge text and which actions are available.
export function PendingChatBubble({ chat, memberId }: { chat: TimelineChat; memberId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const pending = chat.pending!;
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedDate, setReschedDate] = useState(
    pending.sendAt ? new Date(pending.sendAt).toISOString().slice(0, 10) : "",
  );
  const [reschedTime, setReschedTime] = useState(
    pending.sendAt ? new Date(pending.sendAt).toTimeString().slice(0, 5) : "",
  );
  const [reschedError, setReschedError] = useState("");
  const [padsplitErrorOpen, setPadsplitErrorOpen] = useState(false);

  const cancelMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => apiRequest("POST", `/api/app/messages/pending/${pending.id}/cancel`),
    onSuccess: () => {
      toast({ title: "Cancelled" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
    },
    onError: (err) => toast({ title: "Couldn't cancel", description: parseErrorMessage(err, "Couldn't cancel. Please try again."), variant: "destructive" }),
  });

  const sendNowMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/app/messages/pending/${pending.id}/confirm`),
    onSuccess: () => {
      toast({ title: "Sending now" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
    },
    onError: (err) => toast({ title: "Couldn't send", description: parseErrorMessage(err, "Couldn't send. Please try again."), variant: "destructive" }),
  });

  const rescheduleMutation = useMutation({
    mutationFn: async (sendAt: string) =>
      apiRequest("PATCH", `/api/app/messages/pending/${pending.id}`, { sendAt }),
    onSuccess: () => {
      toast({ title: "Rescheduled" });
      setReschedOpen(false);
      setReschedError("");
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
    },
    onError: (err) => toast({ title: "Couldn't reschedule", description: parseErrorMessage(err, "Couldn't reschedule. Please try again."), variant: "destructive" }),
  });

  // Task #1556 — Retry failed PadSplit sends.
  const retryMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => apiRequest("POST", `/api/app/messages/pending/${pending.id}/retry`),
    onSuccess: () => {
      toast({ title: "Retrying…", description: "Your message is being sent via PadSplit Messenger." });
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
    },
    onError: (err) => {
      if (isPadsplitSendError(err)) {
        setPadsplitErrorOpen(true);
        return;
      }
      toast({ title: "Retry failed", description: parseErrorMessage(err, "Couldn't retry. Please try again."), variant: "destructive" });
    },
  });

  function handleReschedConfirm() {
    if (!reschedDate) { setReschedError("Pick a date."); return; }
    const iso = `${reschedDate}T${reschedTime || "00:00"}`;
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) { setReschedError("Invalid date/time."); return; }
    if (dt <= new Date()) { setReschedError("Schedule time must be in the future."); return; }
    rescheduleMutation.mutate(dt.toISOString());
  }

  // Status-driven copy/styling. Pending bubbles are always admin-side.
  const status = pending.status;
  const sendAtLabel = pending.sendAt ? new Date(pending.sendAt).toLocaleString() : "soon";
  // PadSplit send-UX — a healthy queued row (and one still being retried
  // silently in the background) reads as a calm "Sent"; the "Sending…" spinner
  // is gone from the hot path. "failed" is now TERMINAL only.
  // Quo messaging — the transport stamped on the pending row names the
  // channel in the failed state ("via Quo" vs "via PadSplit").
  const failedVia = pending.transport === "quo" ? "Quo" : "PadSplit";
  const badgeText =
    status === "failed" ? `Failed to send via ${failedVia}`
    : status === "scheduled" ? `Scheduled for ${sendAtLabel}`
    : status === "awaiting" ? "Awaiting approval"
    : "Sent";
  // An in-flight BROADCAST must already look like a broadcast. The server
  // ships isBroadcast/broadcastId and the resolved senderName on the pending
  // row, but this component used to ignore chat.workflow entirely — so a
  // freshly-sent broadcast rendered plain blue with no sender and only turned
  // pink (and gained a name) once the harvest + reconcile replaced it with the
  // delivered row, which reads as a glitch.
  const isBroadcast = chat.workflow?.isBroadcast === true;
  const bubbleClass =
    status === "failed"
      ? "bg-red-500/80 text-white border border-red-300/60"
      : isBroadcast
        ? "bg-pink-500 text-white border border-pink-400"
        : "bg-blue-600 text-white border border-blue-400";
  const headerClass =
    status === "failed" ? "text-red-100" : isBroadcast ? "text-pink-100" : "text-blue-100";
  const urls = attachmentUrlsFor(chat);

  return (
    <div
      className="group mb-1 flex flex-col items-end"
      data-testid={`bubble-pending-${pending.id}`}
      data-broadcast={isBroadcast ? "true" : undefined}
    >
      <div className="relative flex items-start gap-1">
        <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${bubbleClass}`}>
          <div className={`mb-1 flex items-center gap-1.5 text-[11px] ${headerClass}`}>
            {status === "failed" ? (
              <AlertCircle className="h-3 w-3" />
            ) : status === "sending" ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : status === "scheduled" ? (
              <Clock className="h-3 w-3" />
            ) : (
              <CheckCircle className="h-3 w-3" />
            )}
            <span data-testid={`text-pending-status-${pending.id}`}>{badgeText}</span>
            {isBroadcast && (
              <span
                className="inline-flex items-center gap-0.5 rounded bg-white/20 px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide"
                data-testid={`pill-broadcast-pending-${pending.id}`}
              >
                <Megaphone className="h-2.5 w-2.5" />
                Broadcast
              </span>
            )}
            {/* An AI auto-draft keeps its AI label after the timer fires or
                Send now is pressed, until the delivered copy (which carries
                its own "Auto-draft · AI" chip) replaces this bubble. Edited
                drafts are cancelled and re-sent as fresh manual rows, so
                pending.autoDraft is never set on them. */}
            {pending.autoDraft && (
              <span
                className="inline-flex items-center gap-0.5 rounded bg-white/20 px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide"
                title="Written by AI and sent automatically by Auto Draft — no one edited it before it went out."
                data-testid={`pill-auto-draft-pending-${pending.id}`}
              >
                <Sparkles className="h-2.5 w-2.5" />
                Auto-draft · AI
              </span>
            )}
            {/* Sender is resolved server-side on the pending row, so the
                operator's name shows from the first render instead of
                appearing only after reconcile. Broadcasts only: on a normal
                one-off send the host already knows they sent it, and the
                delivered bubble doesn't label its own sends either. */}
            {isBroadcast && chat.senderName && (
              <span className="opacity-90" data-testid={`text-pending-sender-${pending.id}`}>
                · {chat.senderName}
              </span>
            )}
            {chat.createdAt && (
              <span className="opacity-70">· {formatTime(chat.createdAt)}</span>
            )}
          </div>
          {status === "failed" && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <button
                onClick={() => retryMutation.mutate()}
                disabled={retryMutation.isPending}
                className="rounded bg-white/20 px-2 py-0.5 text-[11px] font-medium hover:bg-white/30 disabled:opacity-50 transition-colors"
                data-testid={`button-retry-pending-${pending.id}`}
              >
                {retryMutation.isPending ? "Retrying…" : "Retry"}
              </button>
              <a
                href="https://www.padsplit.com/host/communication/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 rounded bg-white px-2 py-0.5 text-[11px] font-medium text-black hover:bg-gray-100 transition-colors"
                data-testid={`button-send-on-padsplit-${pending.id}`}
              >
                <ExternalLink className="h-2.5 w-2.5" />
                Send on PadSplit
              </a>
              {pending.sendError && (
                <p className="w-full mt-1 text-[10px] text-red-100 opacity-80 line-clamp-2">{pending.sendError}</p>
              )}
            </div>
          )}
          {pending.orphanRetriedAt && (
            <div
              className="mt-1 flex items-center gap-1 text-[10px] opacity-80"
              data-testid={`text-pending-auto-retried-${pending.id}`}
              title={`This message got stuck and was automatically re-sent on ${new Date(pending.orphanRetriedAt).toLocaleString()}.`}
            >
              <RefreshCw className="h-2.5 w-2.5" />
              <span>Auto-resent after it got stuck</span>
            </div>
          )}
          {chat.messageText ? <MessageBody text={urls.length > 0 ? stripMediaUrls(chat.messageText) : chat.messageText} /> : null}
          {urls.length > 0 ? <MessageAttachments urls={urls} messageId={chat.id} tone="dark" /> : null}
        </div>
        {/* 3-dot menu with cancel / reschedule / send-now */}
        <div className="flex shrink-0 items-center opacity-0 group-hover:opacity-100 transition-opacity mt-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                data-testid={`button-pending-actions-${pending.id}`}
                title="Pending message actions"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {status === "failed" && (
                <>
                  <DropdownMenuItem
                    onClick={() => retryMutation.mutate()}
                    disabled={retryMutation.isPending}
                    data-testid={`menu-retry-${pending.id}`}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Retry send
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    asChild
                    data-testid={`menu-send-on-padsplit-${pending.id}`}
                  >
                    <a
                      href="https://www.padsplit.com/host/communication/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Send on PadSplit
                    </a>
                  </DropdownMenuItem>
                </>
              )}
              {(status === "scheduled" || status === "awaiting") && (
                <DropdownMenuItem
                  onClick={() => sendNowMutation.mutate()}
                  disabled={sendNowMutation.isPending}
                  data-testid={`menu-send-now-${pending.id}`}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Send now
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => setReschedOpen(true)}
                disabled={rescheduleMutation.isPending}
                data-testid={`menu-reschedule-${pending.id}`}
              >
                <Clock className="mr-2 h-4 w-4" />
                Reschedule
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
                className="text-red-600 focus:text-red-700"
                data-testid={`menu-cancel-${pending.id}`}
              >
                <X className="mr-2 h-4 w-4" />
                Cancel send
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Reschedule dialog */}
      <Dialog open={reschedOpen} onOpenChange={setReschedOpen}>
        <DialogContent className="max-w-xs" data-testid={`dialog-reschedule-pending-${pending.id}`}>
          <DialogHeader>
            <DialogTitle>Reschedule message</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-600">Date</Label>
              <Input
                type="date"
                value={reschedDate}
                onChange={(e) => { setReschedDate(e.target.value); setReschedError(""); }}
                className="mt-1"
                data-testid={`input-pending-resched-date-${pending.id}`}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Time ({Intl.DateTimeFormat().resolvedOptions().timeZone})</Label>
              <Input
                type="time"
                value={reschedTime}
                onChange={(e) => { setReschedTime(e.target.value); setReschedError(""); }}
                className="mt-1"
                data-testid={`input-pending-resched-time-${pending.id}`}
              />
            </div>
            {reschedError && <p className="text-xs text-red-500">{reschedError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setReschedOpen(false)}>Cancel</Button>
            <Button
              size="sm"
              onClick={handleReschedConfirm}
              disabled={rescheduleMutation.isPending}
              data-testid={`button-pending-resched-confirm-${pending.id}`}
            >
              {rescheduleMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PadsplitSendErrorDialog open={padsplitErrorOpen} onOpenChange={setPadsplitErrorOpen} />
    </div>
  );
}


export function BubbleActionsMenu({ chat, isAwaitingReply, onPushToMaintenance, onLinkExistingTicket, linkedMaintenanceTaskId, onUnlinkMaintenance, onSetDueDate, onAssign, onStartThread, onToggleAwaitingReply, isResolved, onResolve, onUnresolve }: {
  chat: TimelineChat;
  isAwaitingReply: boolean;
  // Task #2989 — inbound (member) bubbles get a resolve/unresolve toggle so the
  // host can clear or re-open the resolution signal straight from the bubble.
  isResolved?: boolean;
  onResolve?: () => void;
  onUnresolve?: () => void;
  onPushToMaintenance: () => void;
  // Task #1303 — opens a searchable picker of open maintenance tickets and
  // links the chosen one to this chat without creating a new ticket.
  onLinkExistingTicket: () => void;
  // Task #1528 — when set, the bubble has an active link to a maintenance
  // ticket and the menu surfaces a "Remove link" action that calls
  // onUnlinkMaintenance (which itself prompts for confirmation).
  linkedMaintenanceTaskId?: number | null;
  onUnlinkMaintenance?: () => void;
  onSetDueDate: () => void;
  onAssign: () => void;
  onStartThread: () => void;
  // Optional — only outbound (admin) bubbles get the awaiting-reply toggle
  // because "awaiting reply" only makes sense when WE are the ones waiting
  // for the member to respond.
  onToggleAwaitingReply?: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
          data-testid={`button-bubble-actions-${chat.id}`}
          title="Message actions"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuItem onClick={onPushToMaintenance} data-testid={`menu-push-to-maintenance-${chat.id}`}>
          <Wrench className="mr-2 h-4 w-4" />
          Push to maintenance ticket
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onLinkExistingTicket} data-testid={`menu-link-existing-ticket-${chat.id}`}>
          <Wrench className="mr-2 h-4 w-4" />
          Link existing ticket
        </DropdownMenuItem>
        {linkedMaintenanceTaskId && onUnlinkMaintenance && (
          // Task #1528 — only available when this bubble is currently linked
          // to a maintenance ticket. The handler shows a confirm() prompt
          // before clearing the link so accidental clicks don't drop the
          // association.
          <DropdownMenuItem
            onClick={onUnlinkMaintenance}
            className="text-red-600 focus:text-red-700"
            data-testid={`menu-unlink-maintenance-${chat.id}`}
          >
            <X className="mr-2 h-4 w-4" />
            Remove link to ticket #{linkedMaintenanceTaskId}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onSetDueDate} data-testid={`menu-set-due-date-${chat.id}`}>
          <Calendar className="mr-2 h-4 w-4" />
          Set due date/time
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onAssign} data-testid={`menu-assign-${chat.id}`}>
          <UserCheck className="mr-2 h-4 w-4" />
          Assign to team member
        </DropdownMenuItem>
        {onToggleAwaitingReply && (
          <DropdownMenuItem onClick={onToggleAwaitingReply} data-testid={`menu-toggle-awaiting-${chat.id}`}>
            <Clock className="mr-2 h-4 w-4" />
            {isAwaitingReply ? "Clear awaiting reply" : "Mark awaiting reply"}
          </DropdownMenuItem>
        )}
        {/* Task #2989 — resolve/unresolve toggle. Only wired on inbound member
            bubbles (handlers are passed only there). */}
        {isResolved && onUnresolve && (
          <DropdownMenuItem onClick={onUnresolve} data-testid={`menu-unresolve-${chat.id}`}>
            <Circle className="mr-2 h-4 w-4" />
            Mark as unresolved
          </DropdownMenuItem>
        )}
        {!isResolved && onResolve && (
          <DropdownMenuItem onClick={onResolve} data-testid={`menu-resolve-${chat.id}`}>
            <CheckCircle className="mr-2 h-4 w-4" />
            Mark as resolved
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onStartThread} data-testid={`menu-start-thread-${chat.id}`}>
          <MessageCircle className="mr-2 h-4 w-4" />
          Start internal thread
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


// ─── Assignee picker (team members with messaging access) ─────────────────────
export type AppUserRow = { id: number; name: string | null; fullName: string | null; email: string | null };

// Subset of the `/api/app/team-members` roster row the picker relies on. The
// route returns a synthetic owner entry (role "owner", id 0) plus the real
// team_members rows; `userId` is the owner's app-user id and `inviteeUserId` is
// the member's app-user id once they accept.
type TeamMemberRosterRow = {
  id: number;
  userId: number;
  inviteeUserId?: number | null;
  email?: string | null;
  name?: string | null;
  role: string;
};


export function AssigneePicker({
  open, onOpenChange, currentAssigneeId, currentAssigneeName, onAssign, onClear, chatId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currentAssigneeId: number | null;
  currentAssigneeName: string | null;
  onAssign: (id: number, name: string) => void;
  onClear: () => void;
  chatId: number;
}) {
  // The team roster comes from `/api/app/team-members` (the canonical roster
  // route). Its rows are team_members records — keyed by the membership row id,
  // not the app-user id we assign chats to — so we remap each row to the actual
  // app user: the synthetic owner row (role "owner", id 0) carries the owner's
  // app-user id in `userId`, and accepted member rows carry theirs in
  // `inviteeUserId`. Pending/invited members have no app-user id yet and are
  // skipped (chats can only be assigned to users who have accepted).
  const teamQuery = useQuery<TeamMemberRosterRow[]>({
    queryKey: ["/api/app/team-members"],
    enabled: open,
  });
  // Fetch the current user so we can always offer "Assign to me" — even on
  // brand-new accounts that don't yet have any teammates configured.
  const meQuery = useQuery<{ user: { id: number; name?: string | null; fullName?: string | null; email?: string | null } }>({
    queryKey: ["/api/app/auth/me"],
    enabled: open,
  });
  const me = meQuery.data?.user;
  const users: AppUserRow[] = (teamQuery.data ?? [])
    .map((m): AppUserRow | null => {
      const appUserId = m.role === "owner" ? m.userId : m.inviteeUserId;
      if (appUserId == null) return null;
      return { id: appUserId, name: m.name ?? null, fullName: null, email: m.email ?? null };
    })
    .filter((u): u is AppUserRow => u !== null);
  // De-dup so "me" isn't shown twice when the current user also appears in the team roster.
  const teammates = me ? users.filter((u) => u.id !== me.id) : users;
  const meDisplayName = me ? (me.name ?? me.fullName ?? me.email ?? `User #${me.id}`) : null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger className="hidden" />
      <PopoverContent className="w-64 p-2" side="top" data-testid={`popover-assignee-${chatId}`}>
        <div className="mb-2 px-1 text-sm font-medium">Assign chat</div>
        {me && (
          <button
            className={`w-full flex items-center gap-2 rounded px-2 py-1.5 text-sm text-left hover:bg-gray-100 ${currentAssigneeId === me.id ? "bg-blue-50 text-blue-700" : ""}`}
            onClick={() => { onAssign(me.id, meDisplayName!); onOpenChange(false); }}
            data-testid={`button-assignee-self`}
          >
            <User className="h-3.5 w-3.5 shrink-0 text-blue-500" />
            <span>Assign to me <span className="text-gray-400">({meDisplayName})</span></span>
            {currentAssigneeId === me.id && <CheckCircle className="ml-auto h-3.5 w-3.5 text-blue-500" />}
          </button>
        )}
        {teammates.length > 0 && (
          <div className="mt-2 mb-1 px-1 text-[11px] uppercase tracking-wide text-gray-400">Team members</div>
        )}
        {teamQuery.isLoading ? (
          <div className="py-2 text-xs text-gray-400">Loading team…</div>
        ) : teammates.length === 0 ? (
          !me ? (
            <div className="py-2 text-xs text-gray-400">Loading…</div>
          ) : null
        ) : (
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {teammates.map((u) => {
              const displayName = u.name ?? u.fullName ?? u.email ?? `User #${u.id}`;
              const isSelected = u.id === currentAssigneeId;
              return (
                <button
                  key={u.id}
                  className={`w-full flex items-center gap-2 rounded px-2 py-1.5 text-sm text-left hover:bg-gray-100 ${isSelected ? "bg-blue-50 text-blue-700" : ""}`}
                  onClick={() => { onAssign(u.id, displayName); onOpenChange(false); }}
                  data-testid={`button-assignee-${u.id}`}
                >
                  <User className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                  {displayName}
                  {isSelected && <CheckCircle className="ml-auto h-3.5 w-3.5 text-blue-500" />}
                </button>
              );
            })}
          </div>
        )}
        {currentAssigneeId && (
          <div className="mt-1 border-t pt-1">
            <button
              className="w-full text-left px-2 py-1 text-xs text-red-500 hover:bg-red-50 rounded"
              onClick={() => { onClear(); onOpenChange(false); }}
            >
              Remove assignee
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
